import type { DataEntityDto, DataTypeDto, DataTypeLinkDto } from '@bk/contracts';

/**
 * I legami tra tipi nella pagina dei dati (ADR 0238, tappa 3): il genitore e i legami che devono esserci si vedono come
 * colonne e si scelgono nel modulo; la scheda del genitore mostra le sue voci figlie. Le voci legate stanno in
 * `voci_legate`, per significato del legame, con il nome dell'altra voce.
 */

const fold = (v: unknown) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const RANK: Record<string, number> = { genitore: 0, obbligatorio: 1, alternativa: 2 };

/**
 * I legami che devono esserci, il genitore per primo: quelli che la lista mostra e il modulo fa scegliere. Un legame
 * «molti» obbligatorio vuol dire «almeno uno» (ADR 0238, tappa 4).
 */
export function requiredLinks(type: Pick<DataTypeDto, 'legami_tipo'>): DataTypeLinkDto[] {
  return (type.legami_tipo ?? []).filter((l) => l.obbligo && l.obbligo in RANK).sort((a, b) => RANK[a.obbligo!]! - RANK[b.obbligo!]!);
}

/** Le voci nominate da un legame: una, o più di un legame «molti» separate da «;». */
export const linkedNames = (value: string | null | undefined): string[] => (value ?? '').split(/\s*;\s*/).map((x) => x.trim()).filter(Boolean);

/** La voce di un tipo con questo nome (o uno dei suoi altri nomi). */
export function entityNamed(type: Pick<DataTypeDto, 'entita'> | undefined, name: string | null | undefined): DataEntityDto | undefined {
  if (!type || !name?.trim()) return undefined;
  return type.entita.find((e) => fold(e.nome) === fold(name) || e.altri_nomi.some((n) => fold(n) === fold(name)));
}

/** Il nome del legame nella colonna e nel modulo: «Su · Conto», o solo «Conto» quando il legame si chiama come il tipo. */
export function linkLabel(l: DataTypeLinkDto, types: Array<Pick<DataTypeDto, 'chiave' | 'etichetta'>>): string {
  const target = types.find((t) => t.chiave === l.tipo)?.etichetta ?? l.tipo;
  const name = l.significato.charAt(0).toUpperCase() + l.significato.slice(1);
  return name.toLowerCase() === target.toLowerCase() ? target : `${name} · ${target}`;
}

/** Le voci figlie di una voce (i movimenti nella scheda del conto): per ogni tipo che la ha come genitore, le sue voci. */
export function childrenOf(
  types: DataTypeDto[],
  typeKey: string,
  e: Pick<DataEntityDto, 'rif' | 'nome' | 'altri_nomi'>,
): Array<{ type: DataTypeDto; link: DataTypeLinkDto; items: DataEntityDto[] }> {
  const names = new Set([e.nome, ...e.altri_nomi].map(fold));
  const out: Array<{ type: DataTypeDto; link: DataTypeLinkDto; items: DataEntityDto[] }> = [];
  for (const t of types)
    for (const l of t.legami_tipo ?? []) {
      if (l.obbligo !== 'genitore' || l.tipo !== typeKey) continue;
      const items = t.entita.filter((x) => !(t.chiave === typeKey && x.rif === e.rif) && names.has(fold(x.voci_legate?.[l.significato])));
      if (items.length) out.push({ type: t, link: l, items });
    }
  return out;
}

/**
 * Una voce registrata senza il suo genitore (ADR 0238 § 3): entrata prima che il genitore fosse richiesto, o scritta a
 * mano. Non si cancella: la pagina la elenca, e la persona sceglie il genitore nel modulo.
 */
export function withoutParent(type: Pick<DataTypeDto, 'legami_tipo'>, e: Pick<DataEntityDto, 'voci_legate'>): boolean {
  const parents = (type.legami_tipo ?? []).filter((l) => l.obbligo === 'genitore');
  return parents.length > 0 && !parents.some((l) => !!e.voci_legate?.[l.significato]?.trim());
}
